// The AWP packages backend. Same request shape as the Projects client next door.

import { VerityApiError } from "../StandardsLibrary/api";
import { AwpFindingStatus, AwpRun, PackageDocument, PackageDraft, WorkArea, WorkPackage } from "./types";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/verity`;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // `...init` must come BEFORE headers, or its own `headers` key replaces the merged object outright.
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    const detail = await res
      .json()
      .then((body) => (body as { error?: string }).error)
      .catch(() => undefined);
    throw new VerityApiError(detail || `Request failed (${res.status})`, res.status);
  }

  return (await res.json()) as T;
}

/** The register, with the project's work areas — the form picks from them, the hierarchy groups by them. */
export async function listPackages(
  projectId: string,
): Promise<{ packages: WorkPackage[]; cwas: WorkArea[]; extractorVersion: number }> {
  const { items, cwas, extractorVersion } = await request<{
    items: WorkPackage[];
    cwas: WorkArea[];
    extractorVersion?: number;
  }>(`/projects/${projectId}/packages`);
  // A server that predates the Documents stage sends no version; treating that as 0 marks every
  // read stale, which is the safe way round — it offers a re-read rather than hiding one.
  return { packages: items || [], cwas: cwas || [], extractorVersion: extractorVersion ?? 0 };
}

/**
 * Creates one package per dropped file, typed and titled from the file. Each comes back marked as
 * needing its details checked; files that could not be added are listed with the reason.
 */
export async function uploadPackages(
  projectId: string,
  files: File[],
): Promise<{ created: WorkPackage[]; failed: { fileName: string; error: string }[] }> {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  const res = await fetch(`${BASE}/projects/${projectId}/packages/upload`, {
    method: "POST",
    credentials: "include",
    body: form, // no Content-Type: the browser sets the multipart boundary
  });
  const body = (await res.json().catch(() => ({}))) as {
    created?: WorkPackage[];
    failed?: { fileName: string; error: string }[];
    error?: string;
  };
  if (!res.ok && !body.created?.length && !body.failed?.length) {
    throw new VerityApiError(body.error || `Upload failed (${res.status})`, res.status);
  }
  return { created: body.created ?? [], failed: body.failed ?? [] };
}

export async function createPackage(projectId: string, draft: PackageDraft): Promise<WorkPackage> {
  const { package: pkg } = await request<{ package: WorkPackage }>(
    `/projects/${projectId}/packages`,
    { method: "POST", body: JSON.stringify(draft) },
  );
  return pkg;
}

export async function updatePackage(
  projectId: string,
  packageId: string,
  draft: PackageDraft,
): Promise<WorkPackage> {
  const { package: pkg } = await request<{ package: WorkPackage }>(
    `/projects/${projectId}/packages/${packageId}`,
    { method: "PUT", body: JSON.stringify(draft) },
  );
  return pkg;
}

/**
 * Sets which CWPs an EWP or PWP is linked to, from the EWP's or PWP's side. The links are stored on
 * the CWPs; this returns every CWP that changed so the register can be updated in place.
 */
export async function setPackageCwps(
  projectId: string,
  packageId: string,
  cwpIds: string[],
): Promise<WorkPackage[]> {
  const { changed } = await request<{ changed: WorkPackage[] }>(
    `/projects/${projectId}/packages/${packageId}/cwps`,
    { method: "PUT", body: JSON.stringify({ cwpIds }) },
  );
  return changed || [];
}

export type PackagePreview = { kind: "pdf" } | { kind: "html"; html: string } | { kind: "none" };

/** How the package's source document can be shown: a PDF as-is, Word / Excel as plain HTML. */
export async function getPackagePreview(projectId: string, packageId: string): Promise<PackagePreview> {
  return request<PackagePreview>(`/projects/${projectId}/packages/${packageId}/preview`);
}

/** The package's source document as a File — what the PDF viewer reads. */
export async function fetchPackageFile(projectId: string, pkg: WorkPackage): Promise<File> {
  const res = await fetch(`${BASE}/projects/${projectId}/packages/${pkg.id}/file`, { credentials: "include" });
  if (!res.ok) throw new VerityApiError(`Could not load the document (${res.status})`, res.status);
  const blob = await res.blob();
  return new File([blob], pkg.source?.fileName ?? "document", { type: pkg.source?.contentType || blob.type });
}

export function packageFileUrl(projectId: string, packageId: string): string {
  return `${BASE}/projects/${projectId}/packages/${packageId}/file`;
}

export async function deletePackage(projectId: string, packageId: string): Promise<void> {
  await request<void>(`/projects/${projectId}/packages/${packageId}`, { method: "DELETE" });
}

// ─── Documents stage ────────────────────────────────────────────────────────────

/**
 * Read the package's document into facts, and get both the updated package and what was read.
 *
 * Returns the whole extraction so the screen can show it straight away: a reviewer who presses
 * Read wants to see what came out, not to wait for a second round trip to fetch it.
 */
export async function readPackageDocument(
  projectId: string,
  packageId: string,
): Promise<{ package: WorkPackage; document: PackageDocument }> {
  return request<{ package: WorkPackage; document: PackageDocument }>(
    `/projects/${projectId}/packages/${packageId}/read`,
    { method: "POST" },
  );
}

/** What was read out of a package's document, on a later visit. */
export async function getPackageDocument(
  projectId: string,
  packageId: string,
): Promise<PackageDocument> {
  const { document } = await request<{ document: PackageDocument }>(
    `/projects/${projectId}/packages/${packageId}/document`,
  );
  return document;
}

/** Give a package a document, or replace the one it has. Any earlier read is dropped with it. */
export async function attachPackageDocument(
  projectId: string,
  packageId: string,
  file: File,
): Promise<WorkPackage> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${BASE}/projects/${projectId}/packages/${packageId}/document`, {
    method: "POST",
    credentials: "include",
    body: form, // no Content-Type: the browser sets the multipart boundary
  });
  const body = (await res.json().catch(() => ({}))) as { package?: WorkPackage; error?: string };
  if (!res.ok || !body.package) {
    throw new VerityApiError(body.error || `Could not attach the document (${res.status})`, res.status);
  }
  return body.package;
}

// ─── Review Engine ──────────────────────────────────────────────────────────────

/**
 * Check the chosen packages against each other and against the chosen standards.
 *
 * Fast and deterministic — no polling, no progress to report. Omitting packageIds checks every
 * read package; omitting ruleSetIds still compares the packages, it just cannot report coverage of
 * a standard, because no standard was named.
 */
export async function runAwpReview(
  projectId: string,
  input: { packageIds?: string[]; ruleSetIds?: string[] } = {},
): Promise<AwpRun> {
  const { run } = await request<{ run: AwpRun }>(`/projects/${projectId}/packages/review`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return run;
}

/** Accept a finding, mark it a false positive or waive it. Returns the run with the decision in it. */
export async function decideAwpFinding(
  projectId: string,
  findingId: string,
  status: AwpFindingStatus,
  note: string,
): Promise<AwpRun> {
  const { run } = await request<{ run: AwpRun }>(
    `/projects/${projectId}/packages/findings/${encodeURIComponent(findingId)}/decide`,
    { method: "POST", body: JSON.stringify({ status, note }) },
  );
  return run;
}
